import { resolveFirstRechargeMinSol } from "./engine-gas-config";

/** Ordered keys for every engine’s feature block */
export const ENGINE_FEATURE_KEYS = [
  "volume",
  "makers",
  "trade",
  "cost",
  "firstRecharge",
];

/** Short labels shown in the UI */
export const ENGINE_FEATURE_LABELS = {
  volume: "Vol",
  makers: "Makers",
  trade: "Trade",
  cost: "Cost",
  firstRecharge: "First charge",
};

/**
 * @typedef {{ volume: string, makers: string, trade: string, cost: string }} EngineFeatures
 */

/** Canonical Mini Buy id (API / DB); legacy `V12_MINI_BUY` still accepted. */
export const MINI_BUY_ENGINE_ID = "MiniBuy";

export function isMiniBuyEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === MINI_BUY_ENGINE_ID) return true;
  const u = s.toUpperCase();
  return u === "V12_MINI_BUY" || u === "MINI_BUY" || u === "MINIBUY";
}

/** Canonical Mini Buy-Sell id (API / DB). */
export const MINI_BUY_SELL_ENGINE_ID = "MiniBuySell";

export function isMiniBuySellEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === MINI_BUY_SELL_ENGINE_ID) return true;
  const u = s.toUpperCase();
  return u === "MINI_BUY_SELL" || u === "MINIBUYSELL";
}

/** Canonical Mini Buy-Sell X id (API / DB). */
export const MINI_BUY_SELL_X_ENGINE_ID = "MiniBuySellX";

export function isMiniBuySellXEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === MINI_BUY_SELL_X_ENGINE_ID) return true;
  const u = s.toUpperCase();
  return u === "MINI_BUY_SELL_X" || u === "MINIBUYSELLX";
}

/** Canonical V12 Turbo id (API / DB); matches backend `v12-turbo-engine-id.js`. */
export const V12_TURBO_ENGINE_ID = "V12Turbo";

export function isV12TurboEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === V12_TURBO_ENGINE_ID) return true;
  const u = s.toUpperCase().replace(/[\s_-]+/g, "");
  return u === "V12TURBO";
}

/** Canonical V10 id. */
export const V10_ENGINE_ID = "V10";

export function isV10Engine(engine) {
  const s = String(engine ?? "").trim();
  if (s === V10_ENGINE_ID) return true;
  return s.toUpperCase().replace(/[\s_-]+/g, "") === "V10";
}

/** Combo bundle: 2×V8 + Mini Buy/Sell (single dashboard card). */
export const PORTFOLIO_COMBO_ENGINE_ID = "PortfolioCombo";

export function isPortfolioComboEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === PORTFOLIO_COMBO_ENGINE_ID) return true;
  const u = s.toUpperCase().replace(/[\s_-]+/g, "");
  return u === "PORTFOLIOCOMBO";
}

/** Canonical V4 Eco id (API / DB); matches backend `v4-eco-engine-id.js`. */
export const V4_ECO_ENGINE_ID = "V4Eco";

export function isV4EcoEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === V4_ECO_ENGINE_ID) return true;
  const u = s.toUpperCase().replace(/[\s_-]+/g, "");
  return u === "V4ECO";
}

/** Pair bot: V4 Eco trades with the user's second token instead of IDX. */
export const PAIR_ENGINE_ID = "Pair";

/**
 * Pair user-facing cost (Eco burn 0.032/hr adjusted for 20% platform fee vs Eco 2.7%).
 * Factor ≈ 0.973/0.80 → ~0.04 SOL/hour, ~0.96 SOL/day.
 */
export const PAIR_HOURLY_GAS_SOL = 0.04;
export const PAIR_SUGGESTED_24H_GAS_SOL = 0.96;

export function isPairEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === PAIR_ENGINE_ID) return true;
  const u = s.toUpperCase().replace(/[\s_-]+/g, "");
  return u === "PAIR" || u === "PAIRBOT";
}

export function isV4EcoStyleEngine(engine) {
  return isV4EcoEngine(engine) || isPairEngine(engine);
}

/** Shared dashboard patterns (high swap rate, owner SOL box, etc.). */
export function isMiniBuyFamilyEngine(engine) {
  return (
    isMiniBuyEngine(engine) ||
    isMiniBuySellEngine(engine) ||
    isMiniBuySellXEngine(engine)
  );
}

/** Free trial engine (platform-funded, finite trades). */
export const TRIAL_ENGINE_ID = "Trial";

/** Fixed dashboard gas for trial bots (platform-funded; not tied to wallet balance). */
export const TRIAL_STATIC_GAS_LEVEL = 9;

export function buildTrialStaticGasFees() {
  return {
    level: TRIAL_STATIC_GAS_LEVEL,
    color: "green",
    iconGlow: false,
    firstTime: false,
    totalBalance: 5.6,
  };
}

export function isTrialEngine(engine) {
  const s = String(engine ?? "").trim();
  if (s === TRIAL_ENGINE_ID) return true;
  return String(s).toUpperCase() === "TRIAL";
}

function envMinSol(primary, fallback, defaultValue) {
  if (typeof process === "undefined") return defaultValue;
  const raw = [primary, fallback].find(
    (v) => v != null && String(v).trim() !== "",
  );
  if (raw == null) return defaultValue;
  const n = Number(raw);
  return Number.isFinite(n) ? n : defaultValue;
}


/** Display string for engine-card “1st recharge” row (matches recharge modal floors). */
export function formatFirstRechargeSol(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0 SOL";
  const decimals = n >= 1 ? 1 : 2;
  const text = n.toFixed(decimals).replace(/\.?0+$/, "");
  return `${text} SOL`;
}

export function getEngineFirstRechargeDisplay(engineId) {
  const id = String(engineId ?? "").trim();
  if (isTrialEngine(id)) return "0 SOL";
  if (isPortfolioComboEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_PORTFOLIO_COMBO_SOL_AMOUNT,
        server: process.env.MIN_PORTFOLIO_COMBO_SOL_AMOUNT,
      }),
    );
  }
  if (isPairEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_PAIR_FIRST_RECHARGE_SOL,
        server: process.env.MIN_PAIR_FIRST_RECHARGE_SOL,
      }),
    );
  }
  if (isV4EcoEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_V4ECO_FIRST_RECHARGE_SOL,
        server:
          process.env.MIN_V4ECO_FIRST_RECHARGE_SOL ??
          process.env.NEXT_PUBLIC_MIN_V4ECO_SOL_AMOUNT ??
          process.env.MIN_V4ECO_SOL_AMOUNT,
      }),
    );
  }
  if (id === "V4") {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol("V4", {
        public: process.env.NEXT_PUBLIC_MIN_V4_SOL_AMOUNT,
        server: process.env.MIN_V4_SOL_AMOUNT,
      }),
    );
  }
  if (id === "V6") {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol("V6", {
        public: process.env.NEXT_PUBLIC_MIN_V6_SOL_AMOUNT,
        server: process.env.MIN_V6_SOL_AMOUNT,
      }),
    );
  }
  if (id === "V8") {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol("V8", {
        public: process.env.NEXT_PUBLIC_MIN_V8_SOL_AMOUNT,
        server: process.env.MIN_V8_SOL_AMOUNT,
      }),
    );
  }
  if (isV10Engine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol("V10", {
        public: process.env.NEXT_PUBLIC_MIN_V10_SOL_AMOUNT,
        server: process.env.MIN_V10_SOL_AMOUNT,
      }),
    );
  }
  if (id === "V12") {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol("V12", {
        public: process.env.NEXT_PUBLIC_MIN_V12_SOL_AMOUNT,
        server: process.env.MIN_V12_SOL_AMOUNT,
      }),
    );
  }
  if (isMiniBuySellXEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_MINI_BUY_SELL_SOL_AMOUNT,
        server: process.env.MIN_MINI_BUY_SELL_SOL_AMOUNT,
      }),
    );
  }
  if (isMiniBuySellEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_MINI_BUY_SELL_SOL_AMOUNT,
        server: process.env.MIN_MINI_BUY_SELL_SOL_AMOUNT,
      }),
    );
  }
  if (isV12TurboEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_V12_MINI_BUY_SOL_AMOUNT,
        server:
          process.env.MIN_V12_TURBO_SOL_AMOUNT ??
          process.env.MIN_V12_MINI_BUY_SOL_AMOUNT,
      }),
    );
  }
  if (isMiniBuyEngine(id)) {
    return formatFirstRechargeSol(
      resolveFirstRechargeMinSol(id, {
        public: process.env.NEXT_PUBLIC_MIN_V12_MINI_BUY_SOL_AMOUNT,
        server: process.env.MIN_V12_MINI_BUY_SOL_AMOUNT,
      }),
    );
  }
  return formatFirstRechargeSol(
    envMinSol(
      process.env.NEXT_PUBLIC_MIN_SOL_AMOUNT,
      process.env.MIN_SOL_AMOUNT,
      0.5,
    ),
  );
}

export const engines = [
  
 
    {
      id: "Trial",
      isNew: true,
      name: "Trial",
      color: "bg-gradient-to-r from-white via-zinc-100 to-zinc-300",
      borderColor: "border-gray-500",
      bgColor: "bg-zinc-500/10",
      badge: null,
      borderBadge: "Free Trial",
      bestFor: "Try For Free",
      bestFor2: "",
      features: {
        volume: "Demo",
        makers: "Demo trades",
        trade: "Micro buy/sell",
        cost: "Free",
      },
      icon: "🎯",
      video_url:
        "https://res.cloudinary.com/dos3t0yao/video/upload/v1777274215/Minibuy_6Trades_F_ix4761.mp4",
    },
    {
    id: "V12",
    name: "Engine V12",
    color: "bg-gradient-to-r from-zinc-300 via-slate-400 to-slate-700",
    borderColor: "border-zinc-400/35",
    bgColor: "bg-zinc-400/8",
    badge: null,
    bestFor: "Top ranking",
    bestFor2: "",
    features: {
      volume: "15000 SOL / day",
      makers: "19000+ per day",
      trade: "Instantly",
      cost: "~4 SOL / hour",
    },
    icon: "🛸",
    video_url:
      "https://res.cloudinary.com/dvrevmrbp/video/upload/v1785745666/V12_Trades_F_jbalyh.mp4",
  },
  {
    id: "PortfolioCombo",
    name: "Combo",
    color: "bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-600",
    borderColor: "border-fuchsia-500/35",
    bgColor: "bg-fuchsia-500/8",
    badge: null,
    bestFor: "Super Fast + Free Holders",
    bestFor2: "",
    features: {
      volume: "6000 SOL / day",
      makers: "4000+ / day",
      trade: "Parallel runners",
      cost: "~1.1 SOL / hour",
    },
    icon: "🔥",
    video_url:
      "https://res.cloudinary.com/dvrevmrbp/video/upload/v1780563499/Combo_Bot_trades_hio9bz.mp4",
  },
  {
    id: "V10",
    name: "Engine V10",
    color: "bg-gradient-to-r from-amber-300 to-yellow-800",
    borderColor: "border-amber-700/30",
    bgColor: "bg-amber-800/5",
    badge: null,
    bestFor: "Continuous high tx",
    bestFor2: "",
    features: {
      volume: "3000 SOL / day",
      makers: "Unique high tx",
      trade: "Continuous",
      cost: "~1 SOL / hour",
    },
    icon: "💥",
    video_url:
      "https://res.cloudinary.com/dvrevmrbp/video/upload/v1785745666/V12_Trades_F_jbalyh.mp4",
  },
  {
    id: "V8",
    name: "Engine V8",
    color: "bg-gradient-to-r from-orange-400 to-red-600",
    borderColor: "border-orange-400/30",
    bgColor: "bg-orange-400/5",
    badge: null,
    bestFor: "Fast + Free Holders",
    bestFor2: "",
    features: {
      volume: "3000+ SOL / day",
      makers: "4000+ / day",
      trade: "Every 15-30 sec",
      cost: "~0.5 SOL / hour",
    },
    icon: "🚀",
    video_url:
      "https://res.cloudinary.com/dos3t0yao/video/upload/v1777196947/V8_6Trades_hooijw.mp4",
  },
  {
    id: "V6",
    name: "Engine V6",
    color: "bg-gradient-to-r from-purple-400 to-violet-600",
    borderColor: "border-purple-400/30",
    bgColor: "bg-purple-400/5",
    badge: null,
    bestFor: "Trending boost strategy",
    bestFor2: "",
    features: {
      volume: "350 SOL / day",
      makers: "700+ / day",
      trade: "Every 1-5 min",
      cost: "0.15 SOL / hour",
    },
    icon: "🏎️",
    video_url:
      "https://res.cloudinary.com/dos3t0yao/video/upload/v1777196947/V6_6Trades_veqn4k.mp4",
  },
  {
    id: "V4",
    name: "Engine V4",
    color: "bg-gradient-to-r from-blue-400 to-blue-600",
    borderColor: "border-blue-400/30",
    bgColor: "bg-blue-400/5",
    badge: null,
    bestFor: "Low-cost organic flow",
    bestFor2: "",
    features: {
      volume: "100 SOL / day",
      makers: "125+ / day",
      trade: "Every 6-9 min",
      cost: "0.045 SOL / hour",
    },
    icon: "🚶‍♂️‍➡️",
    video_url:
      "https://res.cloudinary.com/dvrevmrbp/video/upload/v1788954550/V4_new_01_s75npb.mp4",
  },
  {
    id: "V4Eco",
    name: "V4 Eco",
    color: "bg-gradient-to-r from-sky-400 to-emerald-600",
    borderColor: "border-sky-400/30",
    bgColor: "bg-sky-400/5",
    bestFor: "20% more gas saving",
    bestFor2: "",
    features: {
      volume: "80 SOL / day",
      makers: "125+ / day",
      trade: "Every 7-10 min",
      cost: "0.032 SOL / hour",
    },
    icon: "🌿",
    video_url:
      "https://res.cloudinary.com/dos3t0yao/video/upload/v1777196947/V4_6Trades_cvrsin.mp4",
  }, {
    id: "MiniBuy",
    isNew: true,
    name: "Mini Buy",
    color: "bg-gradient-to-r from-fuchsia-400 to-rose-600",
    borderColor: "border-fuchsia-400/30",
    bgColor: "bg-fuchsia-400/5",
    badge: null,
    bestFor: "Ultra-fast TXNS",
    bestFor2: "",
    features: {
      volume: "Micro volume",
      makers: "High tx count",
      trade: "~10 swap/min",
      cost: "~0.1 SOL / hour",
    },
    icon: "⚡",
    video_url:
      "https://res.cloudinary.com/dvrevmrbp/video/upload/v1785745666/Mini_Buy_F_o8calm.mp4",
  },
  {
      id: "MiniBuySell",
      isNew: true,
      name: "Mini Buy/Sell",
      color: "bg-gradient-to-r from-[#c4e4bc] to-[#2f5a3a]",
      borderColor: "border-[#6fa372]/45",
      bgColor: "bg-[#6fa372]/12",
      badge: null,
      bestFor: "Micro Buys-Sells",
      bestFor2: "",
      features: {
        volume: "Micro swap",
        makers: "High tx count",
        trade: "~30 swap/min",
        cost: "~0.30 SOL / hour",
      },
      icon: "🧪",
      video_url:
        "https://res.cloudinary.com/dvrevmrbp/video/upload/v1787645445/Mini_BuySell_F2_icypln.mp4",
    },
   
  
  {
    id: "V12Turbo",
    isNew: true,
    name: "V12 Turbo",
    color: "bg-gradient-to-r from-cyan-400 to-teal-600",
    borderColor: "border-cyan-400/30",
    bgColor: "bg-cyan-400/5",
    badge: null,
    bestFor: "Turbo Real Makers",
    bestFor2: "",
    features: {
      volume: "xxx SOL / day",
      makers: "Makers xxxx+ / day",
      trade: "upto ~6 swap/sec",
      cost: "~x.xx SOL / hour",
    },
    icon: "🔥",
    video_url:
      "https://res.cloudinary.com/dos3t0yao/video/upload/v1777274215/Minibuy_6Trades_F_ix4761.mp4",
  },
  {
    id: "Pair",
    name: "Pair",
    isNew: true,
    color: "bg-gradient-to-r from-amber-400 to-violet-600",
    borderColor: "border-amber-400/30",
    bgColor: "bg-amber-400/5",
    bestFor: "2-in-1 · Save 50%",
    bestFor2: "",
    features: {
      volume: "80 SOL per token ",
      makers: "125+ per token ",
      trade: "Every 7-10 min",
      cost: "~0.04 SOL / hour",
    },
    icon: "🔗",
    video_url:
      "https://res.cloudinary.com/dos3t0yao/video/upload/v1777196947/V4_6Trades_cvrsin.mp4",
  },
];

/** Card / badge gradients (hex ≈ Tailwind from-green-400, orange-400, purple-400, blue-400) */
export const ENGINE_ACCENT = {
  V12: {
    glow: "linear-gradient(to bottom right, #d4d4d8, #334155)",
    bar: "linear-gradient(to right, #d4d4d8, #334155)",
  },
  V12_MINI_BUY: {
    glow: "linear-gradient(to bottom right, #e879f9, #e11d48)",
    bar: "linear-gradient(to right, #e879f9, #e11d48)",
  },
  MiniBuy: {
    glow: "linear-gradient(to bottom right, #e879f9, #e11d48)",
    bar: "linear-gradient(to right, #e879f9, #e11d48)",
  },
  MiniBuySell: {
    glow: "linear-gradient(to bottom right, #a8d9a0, #1e4a2a)",
    bar: "linear-gradient(to right, #7fb575, #2d5636)",
  },
  MINI_BUY_SELL: {
    glow: "linear-gradient(to bottom right, #a8d9a0, #1e4a2a)",
    bar: "linear-gradient(to right, #7fb575, #2d5636)",
  },
  V12Turbo: {
    glow: "linear-gradient(to bottom right, #22d3ee, #0d9488)",
    bar: "linear-gradient(to right, #22d3ee, #0d9488)",
  },
  V12_TURBO: {
    glow: "linear-gradient(to bottom right, #22d3ee, #0d9488)",
    bar: "linear-gradient(to right, #22d3ee, #0d9488)",
  },
  V12TURBO: {
    glow: "linear-gradient(to bottom right, #22d3ee, #0d9488)",
    bar: "linear-gradient(to right, #22d3ee, #0d9488)",
  },
  /** @deprecated Alias; prefer `MiniBuy`. */
  MINI_BUY: {
    glow: "linear-gradient(to bottom right, #e879f9, #e11d48)",
    bar: "linear-gradient(to right, #e879f9, #e11d48)",
  },
  V8: {
    glow: "linear-gradient(to bottom right, #fb923c, #dc2626)",
    bar: "linear-gradient(to right, #fb923c, #dc2626)",
  },
  V10: {
    glow: "linear-gradient(to bottom right, #d6bc7a, #6b4f2a)",
    bar: "linear-gradient(to right, #c9a227, #6b4423)",
  },
  V6: {
    glow: "linear-gradient(to bottom right, #c084fc, #7c3aed)",
    bar: "linear-gradient(to right, #c084fc, #7c3aed)",
  },
  V4: {
    glow: "linear-gradient(to bottom right, #60a5fa, #2563eb)",
    bar: "linear-gradient(to right, #60a5fa, #2563eb)",
  },
  V4Eco: {
    glow: "linear-gradient(to bottom right, #38bdf8, #059669)",
    bar: "linear-gradient(to right, #38bdf8, #059669)",
  },
  Pair: {
    glow: "linear-gradient(to bottom right, #fbbf24, #7c3aed)",
    bar: "linear-gradient(to right, #f59e0b, #7c3aed)",
  },
  PortfolioCombo: {
    glow: "linear-gradient(to bottom right, #7c3aed, #c026d3, #e11d48)",
    bar: "linear-gradient(to right, #9333ea, #db2777, #dc2626)",
  },
  Trial: {
    glow: "linear-gradient(to bottom right, #ffffff, #e4e4e7)",
    bar: "linear-gradient(to right, #ffffff, #f4f4f5, #e4e4e7)",
  },
};

/**
 * Normalized rows for mapping in React (stable order).
 * Supports legacy `features: string[]` if any old data slips in.
 */
export function getEngineFeatureRows(engine) {
  if (!engine?.features) return [];
  const raw = engine.features;
  if (Array.isArray(raw)) {
    return raw.map((line, i) => ({
      key: `legacy-${i}`,
      labelKey: null,
      label: "",
      value: String(line),
      legacyLine: true,
    }));
  }
  return ENGINE_FEATURE_KEYS.map((labelKey) => {
    let value = raw[labelKey];
    if (labelKey === "firstRecharge") {
      const override =
        value != null && String(value).trim() !== "" ? String(value).trim() : "";
      value = override || getEngineFirstRechargeDisplay(engine.id);
    }
    if (value == null || String(value).trim() === "") return null;
    return {
      key: labelKey,
      labelKey,
      label: ENGINE_FEATURE_LABELS[labelKey],
      value: String(value).trim(),
      legacyLine: false,
    };
  }).filter(Boolean);
}

/** Volume engines (V4/V6/V8/V12): orchestration-only Last Swaps (not V4 Eco). */
export function isClassicVolumeMainTradeEngine(engineId) {
  const id = String(engineId ?? "").trim().toUpperCase();
  return id === "V4" || id === "V6" || id === "V8" || id === "V12";
}

/** Volume engines (V4/V6/V8/V12): bot dashboard UI differs from mini-buy family. */
const ENGINES_OMIT_COST_FEATURE_LABEL = new Set(["V4", "V4ECO", "V6", "V8", "V12"]);

/** Volume engines (no mini-buy owner SOL chip; hide legacy "+8 SOL" hint on gas row). */
export function isClassicVolumeEngine(engineId) {
  const id = String(engineId ?? "").trim().toUpperCase();
  return ENGINES_OMIT_COST_FEATURE_LABEL.has(id);
}

/** Hourly cost lines use ~ as an estimate marker (bot cards / engine tiles). */
export function formatApproxCostDisplay(value) {
  const s = String(value ?? "").trim();
  if (!s) return s;
  if (s.startsWith("~")) return s;
  if (!/\bSOL\s*\/\s*hour\b/i.test(s)) return s;
  return `~${s}`;
}

/** Engine tile "Best for" copy on narrow viewports (mobile trims). */
export function compactBestForForMobile(text) {
  return String(text ?? "")
    .replace(/ strategy$/, "")
    .replace(/ flow$/, "")
    .replace(/ more /, " ");
}

/** Engine tile copy on narrow viewports (e.g. "Trade Every 15-30 sec" → "Trade on 15-30s"). */
export function compactEngineFeatureLineForMobile(line, labelKey) {
  let compact = String(line ?? "");
  if (labelKey === "makers") {
    compact = compact.replace(/^\s*Makers\s+/, "").trim();
    if (compact) compact = `Maker ${compact}`;
  }
  return compact
    .replace(/First charge/, "1st charge")
    .replace(/Trade\s+Every/, "Trade on")
    .replace(/Trade\s+upto\s+/, "")
    .replace(/ sec/g, "s")
    .trim();
}

/** Single monospace-friendly line (legacy look) */
export function formatEngineFeatureLine(row, _engineId) {
  if (row.legacyLine) {
    const v = String(row.value ?? "").trim();
    if (/\bSOL\s*\/\s*hour\b/i.test(v)) return formatApproxCostDisplay(v);
    return row.value;
  }
  const value =
    row.labelKey === "cost" ? formatApproxCostDisplay(row.value) : row.value;
  if (row.labelKey === "cost") {
    return ` ${value}`;
  }
  if (
    row.labelKey === "makers" &&
    String(value ?? "").trim() === "High tx count"
  ) {
    return ` ${String(value).trim()}`;
  }
  const label = `${row.label}`.padEnd(9);
  return ` ${label} ${value}`;
}

export const getEngineById = (id) => {
  if (id == null || id === "") return undefined;
  let raw = String(id).trim();
  if (isV12TurboEngine(raw)) raw = V12_TURBO_ENGINE_ID;
  if (isPortfolioComboEngine(raw)) raw = PORTFOLIO_COMBO_ENGINE_ID;
  if (isV4EcoEngine(raw)) raw = V4_ECO_ENGINE_ID;
  if (isPairEngine(raw)) raw = PAIR_ENGINE_ID;
  if (isMiniBuyEngine(raw)) raw = MINI_BUY_ENGINE_ID;
  if (isMiniBuySellEngine(raw)) raw = MINI_BUY_SELL_ENGINE_ID;
  if (isTrialEngine(raw)) raw = TRIAL_ENGINE_ID;
  const exact = engines.find((e) => e.id === raw);
  if (exact) return exact;
  const upper = raw.toUpperCase();
  const byUpper = engines.find((e) => e.id.toUpperCase() === upper);
  if (byUpper) return byUpper;
  const numericOnly = raw.match(/^v?(\d+)$/i);
  if (numericOnly) {
    const candidate = `V${numericOnly[1]}`;
    const byNum = engines.find((e) => e.id.toUpperCase() === candidate.toUpperCase());
    if (byNum) return byNum;
  }
  const m = raw.match(/\b(V\d+)\b/i);
  if (m) {
    const code = m[1].toUpperCase();
    return engines.find((e) => e.id.toUpperCase() === code);
  }
  return undefined;
};

/** Accent gradients for any engine id alias (e.g. `12` → V12). */
export function getEngineAccent(engineId) {
  const u = String(engineId ?? "").trim().toUpperCase();
  if (u === "V4ECO" && ENGINE_ACCENT.V4Eco) return ENGINE_ACCENT.V4Eco;
  const cfg = getEngineById(engineId);
  const id = cfg?.id;
  if (id && ENGINE_ACCENT[id]) return ENGINE_ACCENT[id];
  return ENGINE_ACCENT.V8;
}

export const getEngineNames = () => {
  return engines.map((engine) => engine.name);
};

export const getEngineIds = () => {
  return engines.map((engine) => engine.id);
};

export const getEngineByNames = (name) => {
  return engines.find((engine) => engine.name === name);
};

export const getEngineCost = (id) => {
  const engine = getEngineById(id);
  if (!engine?.features) return "Unknown";
  const f = engine.features;
  if (typeof f === "object" && !Array.isArray(f) && f.cost != null) {
    return String(f.cost).trim();
  }
  if (Array.isArray(f)) {
    const costFeature = f.find((line) => String(line).includes("Cost"));
    return costFeature ? String(costFeature).split("Cost")[1]?.trim() ?? "Unknown" : "Unknown";
  }
  return "Unknown";
};

export const getEngineVolume = (id) => {
  const engine = getEngineById(id);
  if (!engine?.features) return "Unknown";
  const f = engine.features;
  if (typeof f === "object" && !Array.isArray(f) && f.volume != null) {
    const v = String(f.volume).trim();
    const afterDollar = v.includes("$") ? v.split("$")[1] : v;
    return afterDollar?.split("/")[0]?.trim() ?? v;
  }
  if (Array.isArray(f)) {
    const volumeFeature = f.find((line) => String(line).includes("Volume"));
    return volumeFeature
      ? String(volumeFeature).split("$")[1]?.split("/")[0]?.trim() ?? "Unknown"
      : "Unknown";
  }
  return "Unknown";
};

export default engines;
